import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React, { useEffect } from 'react';
import { CurrencyCode } from '../../../types/currencyCode';
import { PerilUpdateRequest } from '../../../types/policy';
import { formatCurrency, getCurrencySymbol } from '../../../util/util';
import ReactSelect, { SelectValue } from '../fields/ReactSelect';

interface Props {
	name: keyof PerilUpdateRequest;
	id: string;
	label: string;
	value: string;
	currency: CurrencyCode;
	setText: string;
	unsetText: string;
	isInEditMode: boolean;
	removeKey: string;
	unlimitedText?: string;
	userHasWriteAccess?: boolean;
	updateLimit: (name: string, value: string) => void;
}

const PerilLimitSelect = (props: Props) => {
	const {
		id,
		unlimitedText,
		setText,
		unsetText,
		name,
		label,
		value,
		currency,
		isInEditMode,
		updateLimit,
	} = props;

	const initialLimitVal = value && value !== 'unlimited' ? value : null;
	const initialLimitType =
		value === 'unlimited' ? unlimitedText : value ? setText : unsetText;

	const [limitVal, setLimitVal] = React.useState(initialLimitVal);
	const [selectValue, setSelectValue] = React.useState(initialLimitType);

	const limitOptions = unlimitedText
		? [setText, unsetText, unlimitedText]
		: [setText, unsetText];

	useEffect(() => {
		const updatedLimitVal = value && value !== 'unlimited' ? value : null;
		const updatedLimitType =
			value === 'unlimited' ? unlimitedText : value ? setText : unsetText;
		setLimitVal(updatedLimitVal);
		setSelectValue(updatedLimitType);
	}, [value]);

	const updateSelect = (selectedVal: string) => {
		setSelectValue(selectedVal);
		update(selectedVal);
	};

	const updateValue = (newVal: string) => {
		setLimitVal(newVal);
		update(null, newVal);
	};

	const update = (selected?: string, newLimitVal?: string) => {
		const selectedVal = selected ? selected : selectValue;
		if (selectedVal === setText) {
			const limit = newLimitVal ? newLimitVal : limitVal;
			updateLimit(name, limit);
		} else if (selectedVal === unlimitedText) {
			updateLimit(name, 'unlimited');
		} else {
			updateLimit(name, null);
		}
	};

	const renderLimitType = () => {
		return (
			<div style={{ width: '30%', marginRight: '20px' }}>
				<ReactSelect
					id={`${id}-${name}-type`}
					isDisabled={!isInEditMode}
					options={limitOptions.map((op) => ({ value: op, label: op }))}
					value={{ value: selectValue, label: selectValue }}
					onChange={(opt: SelectValue) => updateSelect(opt.value)}
				/>
			</div>
		);
	};

	const renderLimitValue = () => {
		if (selectValue === unsetText || selectValue === unlimitedText) {
			return null;
		}
		const currencyVal = getCurrencySymbol(currency);

		return (
			<TextField
				id={`${id}-${name}`}
				style={{ width: '30%' }}
				label={null}
				value={
					isInEditMode
						? limitVal
						: formatCurrency(parseFloat(limitVal), currency)
				}
				disabled={!isInEditMode}
				onChange={(e) => updateValue(e.target.value)}
				InputProps={
					isInEditMode
						? {
								startAdornment: (
									<InputAdornment position='start'>
										{currencyVal}
									</InputAdornment>
								),
						  }
						: null
				}
			/>
		);
	};

	return (
		<div
			style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '20px' }}
		>
			<div style={{ width: '25%' }}>{label}</div>
			{renderLimitType()}
			{renderLimitValue()}
		</div>
	);
};

export default PerilLimitSelect;
